import './Lobby.css'

import {
  createGame,
  GameInfo,
  GameRules,
  User,
  UserInfo,
} from '@kfiroo/backgammon-core'
import { doc, getDoc } from 'firebase/firestore'
import { forEach, orderBy } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'

import { AuthResult } from './auth/useFirebaseAuth'
import { TimestampLike, toDate } from './firebase/converters'
import { useFirestore } from './firebase/FirebaseProvider'
import { useToast } from './Game/components/Toast'
import { createFirestoreGameRepository } from './Game/services/GameRepository'
import dice from './images/dice.png'
import logo from './images/logo.png'

export function Lobby({ user, loading, errors, signInWithGoogle }: AuthResult) {
  const firestore = useFirestore()
  const { showError } = useToast()
  const startGame = useCallback(
    async (rules: GameRules) => {
      const model = createGame(rules)
      const ref = doc(firestore, 'games', model.gameId)
      try {
        await createFirestoreGameRepository(ref).save(model)
        window.location.assign(`?gameId=${model.gameId}`)
      } catch {
        showError('Could not start the game — try again')
      }
    },
    [firestore, showError],
  )

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  useEffect(() => {
    if (user) {
      const ref = doc(firestore, 'users', user.uid)
      getDoc(ref)
        .then((doc) => {
          const d = doc.data() as UserInfo
          forEach(d.games, (g) => {
            g.joinedAt = toDate(g.joinedAt as unknown as TimestampLike)
          })
          setUserInfo(d)
        })
        .catch((err) => {
          console.error('getDoc', err)
          showError('Could not load your games — try again')
        })
    }
  }, [user, firestore, showError])

  const gameEntries = Object.entries(userInfo?.games || {})
  const gamesList = orderBy(
    gameEntries,
    [([, g1]) => g1.joinedAt.getTime()],
    ['desc'],
  )

  return (
    <div className="lobby">
      <div className="logo">
        <img src={logo} alt="logo" />
        <img src={dice} alt="dice" style={{ display: 'none' }} />
      </div>

      <div className="login">
        <Login
          user={user}
          loading={loading}
          errors={errors}
          signInWithGoogle={signInWithGoogle}
        />
      </div>

      <div className="buttons">
        <button
          className="btn"
          disabled={user === undefined}
          onClick={() => startGame('standard')}
        >
          Start Normal Game
        </button>
        <button
          className="btn"
          disabled={user === undefined}
          onClick={() => startGame('roro')}
        >
          Start Roroyal Game
        </button>
      </div>

      <div className="games">
        {gamesList.map(([gameId, game]) => (
          <GameListItem key={gameId} gameId={gameId} game={game} user={user!} />
        ))}
      </div>
    </div>
  )
}

function GameListItem({
  gameId,
  game,
  user,
}: {
  gameId: string
  game: GameInfo
  user: User
}) {
  const playerIndex = game.players.findIndex((u) => u?.email === user.email)
  const gameStatus =
    game.state === 'game-over'
      ? game.winner === playerIndex
        ? 'winner'
        : 'loser'
      : game.state

  const player = game.players[playerIndex]
  const opponent = game.players[1 - playerIndex]

  return (
    <div
      className={`game-list-item ${gameStatus} ${
        playerIndex === -1
          ? ''
          : playerIndex === game.turn
            ? 'player-turn'
            : 'opponent-turn'
      }`}
      onClick={() => window.location.assign(`?gameId=${gameId}`)}
      role="button"
      aria-hidden
    >
      {player && (
        <div className="player">
          <div className="name">{player.displayName}</div>
          <img className="avatar" src={player.photoURL} alt="avatar" />
        </div>
      )}
      <div className="vs">-</div>
      {opponent && (
        <div className="opponent">
          <img className="avatar" src={opponent.photoURL} alt="avatar" />
          <div className="name">{opponent.displayName}</div>
        </div>
      )}
    </div>
  )
}

export function Login({ user, loading, errors, signInWithGoogle }: AuthResult) {
  const { showError } = useToast()
  if (loading) {
    return <div>Loading...</div>
  }
  if (Object.keys(errors).length) {
    console.error('login errors', errors)
    return <div>Login error ya kof metumtam</div>
  }
  if (user) {
    return (
      <div>
        <img src={user.photoURL} alt="profile" />
        Hey {user.displayName}
      </div>
    )
  }
  return (
    <button
      onClick={() =>
        void signInWithGoogle().catch(() =>
          showError('Sign-in failed — try again'),
        )
      }
    >
      Sign In with Google
    </button>
  )
}
